"use client";

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import Modal, { btnPrimary, btnGhost, btnDanger, inputCls, labelCls } from "@/components/finance/Modal";
import { get, post, put, del, can, downloadFile, getCurrentOrganization } from "@/utils/service";
import { apiMessage, fieldErrors, money, shortDate } from "@/utils/api";
import RecordSelect, { type Picked } from "@/components/crm/RecordSelect";
import LineItemsEditor, { linesFromApi, linesToApi, newLine, type LineDraft } from "@/components/crm/LineItemsEditor";
import { QUOTE_STATUS } from "./quoteStatus";
import AppSelect from "@/components/ui/app-select";
import StatusProgress from "@/components/crm/StatusProgress";
import DateInput from "@/components/ui/date-input";

// A quote's normal path; declined, expired and replaced quotes leave it.
const QUOTE_JOURNEY = [
  { key: "draft", label: "Draft" },
  { key: "pending_approval", label: "Approval" },
  { key: "approved", label: "Approved" },
  { key: "sent", label: "Sent" },
  { key: "accepted", label: "Accepted" },
  { key: "converted", label: "Invoiced" },
];

interface Quote {
  id: string; number: string; version: number; title: string; status: string; issue_date: string; expiry_date: string | null; currency: string;
  subtotal: string; discount_total: string; tax_total: string; total: string; notes: string | null; terms: string | null;
  approved_by?: { name: string } | null; approved_at: string | null; sent_at: string | null; accepted_at: string | null; rejected_at: string | null;
  rejection_reason: string | null; converted_invoice_id: string | null;
  account?: { id: string; name: string } | null; contact?: { id: string; name: string; email: string | null } | null; deal?: { id: string; title: string } | null;
  items: Array<Record<string, any>>; can_approve: boolean; can?: { update: boolean; delete: boolean } | null;
}
interface Version { id: string; version: number; status: string; total: string; currency: string; created_at: string }

const today = () => new Date().toISOString().slice(0, 10);

export default function QuoteDetailPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [missing, setMissing] = useState(false);
  const [form, setForm] = useState({ title: "", issue_date: today(), expiry_date: "", currency: getCurrentOrganization()?.base_currency || "UGX", notes: "", terms: "" });
  const [account, setAccount] = useState<Picked>(null);
  const [contact, setContact] = useState<Picked>(null);
  const [deal, setDeal] = useState<Picked>(null);
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [reasonFor, setReasonFor] = useState<"return" | "decline" | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const r = await get<{ data: { quote: Quote } }>(`/quotes/${id}`);
      const q = r.data.quote;
      setQuote(q);
      setForm({ title: q.title, issue_date: q.issue_date, expiry_date: q.expiry_date ?? "", currency: q.currency, notes: q.notes ?? "", terms: q.terms ?? "" });
      setAccount(q.account ? { id: q.account.id, label: q.account.name } : null);
      setContact(q.contact ? { id: q.contact.id, label: q.contact.name } : null);
      setDeal(q.deal ? { id: q.deal.id, label: q.deal.title } : null);
      setLines(q.items.length ? linesFromApi(q.items) : [newLine()]);
      get<{ data: { versions: Version[] } }>(`/quotes/${id}/versions`).then((v) => setVersions(v.data.versions)).catch(() => undefined);
    } catch {
      setMissing(true);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const editable = isNew || (quote?.status === "draft" && quote.can?.update !== false);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErrors({});
    const body = {
      ...form, expiry_date: form.expiry_date || null, notes: form.notes || null, terms: form.terms || null,
      account_id: account?.id ?? null, contact_id: contact?.id ?? null, deal_id: deal?.id ?? null, items: linesToApi(lines),
    };
    try {
      if (isNew) {
        const r = await post<{ data: { quote: Quote } }>("/quotes", body);
        notify("Quote created");
        navigate(`/quotes/${r.data.quote.id}`, { replace: true });
      } else {
        await put(`/quotes/${id}`, body);
        notify("Quote saved");
        load();
      }
      return true;
    } catch (e) {
      setErrors(fieldErrors(e));
      notify(apiMessage(e, "Could not save the quote."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: string, body: Record<string, unknown> = {}, success?: string) => {
    if (!quote) return;
    setBusy(true);
    try {
      if (action === "submit" && editable && !(await save())) return;
      const r = await post<{ data: { quote: Quote } }>(`/quotes/${quote.id}/${action}`, body);
      notify(success ?? "Quote updated");
      if (action === "revise") navigate(`/quotes/${r.data.quote.id}`);
      else load();
    } catch (e) {
      notify(apiMessage(e, "That step couldn't be completed."));
    } finally {
      setBusy(false);
      setReasonFor(null);
      setReason("");
    }
  };

  const convert = async () => {
    if (!quote || !await confirmDialog(`Create an invoice from ${quote.number} for ${money(quote.total, quote.currency)}?`)) return;
    setBusy(true);
    try {
      const r = await post<{ data: { invoice: { id: string; invoice_number: string } } }>(`/quotes/${quote.id}/convert`, {});
      notify(`Invoice ${r.data.invoice.invoice_number} created`);
      navigate(`/invoices/${r.data.invoice.id}`);
    } catch (e) {
      notify(apiMessage(e, "Could not create the invoice."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!quote || !await confirmDialog(`Delete ${quote.number}?`)) return;
    try {
      await del(`/quotes/${quote.id}`);
      notify("Quote deleted");
      navigate("/quotes");
    } catch (e) {
      notify(apiMessage(e, "Could not delete the quote."));
    }
  };

  if (missing) return <div className="p-10 text-center text-sm text-gray-500">Quote not found. <Link className="text-primary underline" to="/quotes">Back to quotes</Link></div>;
  if (!isNew && !quote) return <div className="p-10 animate-pulse"><div className="h-6 w-48 bg-gray-100 rounded mb-4" /><div className="h-40 bg-gray-100 rounded" /></div>;

  const status = quote ? QUOTE_STATUS[quote.status] ?? QUOTE_STATUS.draft : QUOTE_STATUS.draft;
  const err = (k: string) => errors[k] && <p className="mt-1 text-xs text-red-600">{errors[k]}</p>;
  const mayUpdate = can("quotes.update") && quote?.can?.update !== false;

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full space-y-5">
        <PageHeader title={isNew ? "New quote" : `${quote!.number}${quote!.version > 1 ? ` v${quote!.version}` : ""}`} description={isNew ? "Add lines from your catalogue or type them in" : quote!.title}>
          <Link to="/quotes" className={btnGhost}>All quotes</Link>
          {!isNew && <button className={btnGhost} onClick={() => downloadFile(`/quotes/${quote!.id}/pdf`, `${quote!.number}-v${quote!.version}.pdf`)}>Download PDF</button>}
          {editable && <button className={btnPrimary} disabled={busy || !form.title} onClick={save}>{busy ? "Saving…" : isNew ? "Create quote" : "Save"}</button>}
        </PageHeader>

        {quote && (
          <div className="rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-5 sm:px-6">
            <StatusProgress
              steps={QUOTE_JOURNEY}
              current={quote.status === "superseded" ? "draft" : ["rejected", "expired"].includes(quote.status) ? "sent" : quote.status}
              exit={quote.status === "rejected" ? { label: "Declined" } : quote.status === "expired" ? { label: "Expired" } : quote.status === "superseded" ? { label: "Replaced" } : null}
            />
          </div>
        )}

        {quote && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
              <span className={`inline-flex rounded-full px-2.5 py-0.5 font-medium ${status.cls}`}>{status.label}</span>
              {quote.approved_by && <span>Approved by {quote.approved_by.name}</span>}
              {quote.sent_at && <span>Sent {shortDate(quote.sent_at)}</span>}
              {quote.rejection_reason && <span className="text-red-600">Reason: {quote.rejection_reason}</span>}
              {quote.converted_invoice_id && <Link className="text-primary underline" to={`/invoices/${quote.converted_invoice_id}`}>Open the invoice</Link>}
            </div>
            <div className="flex flex-wrap gap-2">
              {quote.status === "draft" && mayUpdate && <button className={btnPrimary} disabled={busy} onClick={() => act("submit", {}, "Quote submitted")}>Submit</button>}
              {quote.status === "draft" && can("quotes.delete") && quote.can?.delete !== false && <button className={btnDanger} onClick={remove}>Delete</button>}
              {quote.status === "pending_approval" && quote.can_approve && <button className={btnPrimary} disabled={busy} onClick={() => act("approve", {}, "Quote approved")}>Approve</button>}
              {["pending_approval", "approved"].includes(quote.status) && mayUpdate && <button className={btnGhost} disabled={busy} onClick={() => setReasonFor("return")}>Return to draft</button>}
              {quote.status === "approved" && mayUpdate && <button className={btnPrimary} disabled={busy} onClick={() => act("send", {}, "Marked as sent")}>Mark as sent</button>}
              {quote.status === "sent" && mayUpdate && <button className={btnPrimary} disabled={busy} onClick={() => act("accept", {}, "Quote accepted")}>Customer accepted</button>}
              {quote.status === "sent" && mayUpdate && <button className={btnGhost} disabled={busy} onClick={() => setReasonFor("decline")}>Customer declined</button>}
              {["approved", "sent", "rejected", "expired", "pending_approval"].includes(quote.status) && mayUpdate && <button className={btnGhost} disabled={busy} onClick={() => act("revise", {}, "New version created")}>Revise</button>}
              {quote.status === "accepted" && can("invoices.create") && <button className={btnPrimary} disabled={busy} onClick={convert}>Create invoice</button>}
            </div>
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            <section className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="mb-4 text-sm font-semibold text-gray-900">Lines</h2>
              <LineItemsEditor lines={lines} onChange={setLines} currency={form.currency} readOnly={!editable} />
              {err("items")}
              {quote && !editable && (
                <p className="mt-3 text-[11px] text-gray-500">Only drafts can be edited. Use "Revise" to create a new version.</p>
              )}
            </section>
            <section className="rounded-xl border border-gray-200 bg-white p-5 grid gap-4 md:grid-cols-2">
              <div><label className={labelCls}>Notes for the customer</label><textarea rows={4} className={inputCls} disabled={!editable} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
              <div><label className={labelCls}>Terms</label><textarea rows={4} className={inputCls} disabled={!editable} value={form.terms} onChange={(e) => set("terms", e.target.value)} placeholder="Payment terms, validity, delivery…" /></div>
            </section>
          </div>

          <aside className="space-y-5">
            <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
              <div><label className={labelCls}>Title *</label><input className={inputCls} disabled={!editable} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Website redesign" />{err("title")}</div>
              <div><label className={labelCls}>Account</label>{editable ? <RecordSelect light kind="accounts" value={account} onChange={(v) => { setAccount(v); setContact(null); }} /> : <p className="text-xs text-gray-800">{account?.label ?? "—"}</p>}{err("account_id")}</div>
              <div><label className={labelCls}>Contact</label>{editable ? <RecordSelect light kind="contacts" value={contact} onChange={setContact} params={account ? { account_id: account.id } : undefined} /> : <p className="text-xs text-gray-800">{contact?.label ?? "—"}</p>}</div>
              <div><label className={labelCls}>Deal</label>{editable ? <RecordSelect light kind="deals" value={deal} onChange={setDeal} params={account ? { account_id: account.id } : undefined} /> : <p className="text-xs text-gray-800">{deal?.label ?? "—"}</p>}{err("deal_id")}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Issue date</label><DateInput className={inputCls} disabled={!editable} value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></div>
                <div><label className={labelCls}>Valid until</label><DateInput className={inputCls} disabled={!editable} value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} />{err("expiry_date")}</div>
              </div>
              <div><label className={labelCls}>Currency</label><AppSelect className={inputCls} disabled={!editable} value={form.currency} onChange={(e) => set("currency", e.target.value)}>{[...new Set([form.currency, "UGX", "USD", "EUR", "GBP", "KES"])].map((c) => <option key={c}>{c}</option>)}</AppSelect></div>
            </section>

            {quote && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 text-xs">
                <h2 className="mb-3 text-sm font-semibold text-gray-900">Totals</h2>
                <div className="space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{money(quote.subtotal, quote.currency)}</span></div>
                  {Number(quote.discount_total) > 0 && <div className="flex justify-between"><span className="text-gray-500">Discount</span><span>-{money(quote.discount_total, quote.currency)}</span></div>}
                  <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>{money(quote.tax_total, quote.currency)}</span></div>
                  <div className="flex justify-between border-t border-gray-100 pt-1 font-semibold"><span>Total</span><span>{money(quote.total, quote.currency)}</span></div>
                </div>
              </section>
            )}

            {versions.length > 1 && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 text-xs">
                <h2 className="mb-3 text-sm font-semibold text-gray-900">Versions</h2>
                <ul className="space-y-2">
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-center justify-between">
                      <Link className={v.id === quote?.id ? "font-semibold text-gray-900" : "text-primary hover:underline"} to={`/quotes/${v.id}`}>Version {v.version}</Link>
                      <span className="text-gray-500">{(QUOTE_STATUS[v.status] ?? QUOTE_STATUS.draft).label} · {money(v.total, v.currency)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        </div>
      </div>

      <Modal
        open={reasonFor !== null}
        onClose={() => setReasonFor(null)}
        title={reasonFor === "return" ? "Return to draft" : "Customer declined"}
        footer={<>
          <button className={btnGhost} onClick={() => setReasonFor(null)}>Cancel</button>
          <button className={btnPrimary} disabled={busy || (reasonFor === "return" && !reason.trim())} onClick={() => act(reasonFor === "return" ? "return" : "decline", { reason: reason || null }, reasonFor === "return" ? "Returned to draft" : "Marked as declined")}>Confirm</button>
        </>}
      >
        <label className={labelCls}>{reasonFor === "return" ? "What needs to change? *" : "Reason (optional)"}</label>
        <textarea rows={3} className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </div>
  );
}
